export type NavOverflowInput = {
  /** Outer width (content + trailing gap) of each nav item, in px, in DOM order. */
  itemWidths: number[];
  /** Width available to the nav-links container, in px (its constrained grid track). */
  containerWidth: number;
  /** Width the "…" overflow trigger occupies (incl. its gap) when shown, in px. */
  moreButtonWidth: number;
};

export type NavOverflowResult = {
  /** How many leading items stay inline. */
  visibleCount: number;
  /** Items at index >= this go into the overflow menu (always === visibleCount). */
  overflowStartIndex: number;
  hasOverflow: boolean;
};

/**
 * Pure decision for how many leading nav items fit inline vs. collapse into a
 * "…" menu. Greedy from the start; when anything overflows, space for the
 * trigger is reserved. A non-finite, negative, OR ZERO containerWidth is treated
 * as "not laid out yet" and shows everything. A real space-starved links track
 * never reads as exactly 0 — even when tabs must collapse it keeps a positive
 * width (measured: 256px→3 visible, 185px→1) and collapses via the greedy path
 * below. 0 only appears transiently (before `ps-universal-nav` mounts its
 * `navigation` slot, before the web font settles, before late presence avatars
 * widen the neighbouring max-content tracks); treating that transient 0 as
 * "no room → collapse" froze every tab into the "…" menu on desktop
 * (PSTUDIO-3720 regression, commit b79fd9034d). So 0 == "show all"; the
 * ResizeObserver re-runs once the real track width lands.
 *
 * Note: each itemWidth is assumed to already include its trailing gap, and
 * moreButtonWidth its own gap; this can over-reserve by at most one gap, which
 * errs toward collapsing one item earlier — safe (never overflows the track).
 */
export function computeNavOverflow({
  itemWidths,
  containerWidth,
  moreButtonWidth,
}: NavOverflowInput): NavOverflowResult {
  const total = itemWidths.length;

  // A non-finite, negative, or exactly-0 measurement means "not laid out yet"
  // → show everything and let the ResizeObserver re-run when the real width
  // lands. A genuinely space-starved 1fr links track always reads as a POSITIVE
  // width and collapses through the greedy path below; a hard 0 is only ever the
  // pre-layout transient (see the doc comment — PSTUDIO-3720).
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
    return {
      visibleCount: total,
      overflowStartIndex: total,
      hasOverflow: false,
    };
  }

  const sum = itemWidths.reduce((acc, w) => acc + w, 0);
  if (sum <= containerWidth) {
    return {
      visibleCount: total,
      overflowStartIndex: total,
      hasOverflow: false,
    };
  }

  // Overflow: reserve room for the trigger and greedily keep leading items.
  const budget = containerWidth - moreButtonWidth;
  let used = 0;
  let visibleCount = 0;
  for (const w of itemWidths) {
    if (used + w > budget) break;
    used += w;
    visibleCount += 1;
  }

  return { visibleCount, overflowStartIndex: visibleCount, hasOverflow: true };
}

/**
 * The inline `min-width` the hook writes on the links track (`ps-nav-links`).
 *
 * It always reserves the "…" trigger's width, so the 1fr track can never shrink
 * below the only way into the collapsed tabs (PSTUDIO-3720). It is reserved even
 * while every tab fits: with `0px` there, one step from a wide viewport to a
 * narrow one (1920 → 640) let the track shrink to exactly 0, which
 * computeNavOverflow reads as "not laid out yet" and so shows everything —
 * nothing ever collapsed (PSTUDIO-4354). A fitting row is far wider than the
 * trigger, so the reservation costs nothing there. Never '': the track is a
 * grid item, and `min-width: auto` would stop it shrinking below its content.
 * Before the trigger is measured it falls back to `0px`.
 */
export const reservedTrackMinWidth = (moreButtonWidth: number): string =>
  moreButtonWidth > 0 ? `${Math.ceil(moreButtonWidth)}px` : '0px';
